<!-- ============================================================
     user-selection.component.html — VARIATION B
     Two-column layout: stepper + form (main) + sticky navy summary rail.
     Every Angular binding from the original is preserved — only the
     surrounding structure/classes changed. No required .ts changes.
     ============================================================ -->

<div class="selection-page">
  <div class="selection-inner">

    <!-- Page heading -->
    <div class="selection-head">
      <span class="cc-eyebrow">Scheduling</span>
      <h1>Send Calendar Link</h1>
    </div>

    <!-- Progress stepper (driven by existing state, no .ts changes needed) -->
    <ol class="cc-stepper">
      <li class="cc-step" [class.cc-step--done]="selectedCalendar">
        <span class="cc-step__num">{{ selectedCalendar ? '✓' : '1' }}</span>
        <span class="cc-step__label">Calendar</span>
      </li>
      <li class="cc-step" [class.cc-step--done]="selectedMeetingType">
        <span class="cc-step__num">{{ selectedMeetingType ? '✓' : '2' }}</span>
        <span class="cc-step__label">Meeting</span>
      </li>
      <li class="cc-step" [class.cc-step--done]="selectedContact">
        <span class="cc-step__num">{{ selectedContact ? '✓' : '3' }}</span>
        <span class="cc-step__label">Recipient</span>
      </li>
      <li class="cc-step" [class.cc-step--done]="emailContent">
        <span class="cc-step__num">{{ emailContent ? '✓' : '4' }}</span>
        <span class="cc-step__label">Message</span>
      </li>
      <li class="cc-step cc-step--last">
        <span class="cc-step__num">5</span>
        <span class="cc-step__label">Send</span>
      </li>
    </ol>

    <div class="selection-grid">

      <!-- ===================== MAIN COLUMN ===================== -->
      <div class="selection-main">

        <!-- Sender Calendar Selection -->
        <div class="calendar-selection">
          <label class="section-label">Send From (Calendar)</label>

          @if (isLoadingCalendars) {
            <div class="loading-indicator">
              <mat-spinner diameter="24"></mat-spinner>
              <span>Loading your calendars...</span>
            </div>
          }

          @if (!isLoadingCalendars) {
            <ul class="calendar-list">
              @for (calendar of calendars; track calendar.ownerEmail) {
                <li
                  class="calendar-item"
                  [class.selected]="selectedCalendar === calendar"
                  [class.disabled]="isSending"
                  (click)="!isSending && selectCalendar(calendar)">
                  <div class="calendar-icon">
                    @if (calendar.isOwn) {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    } @else {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    }
                  </div>
                  <div class="calendar-info">
                    <span class="calendar-name">{{ calendar.ownerName }}</span>
                    <span class="calendar-email">{{ calendar.ownerEmail }}</span>
                    @if (calendar.isOwn) {
                      <span class="own-badge">Your Calendar</span>
                    } @else {
                      <span class="delegated-badge">Delegated</span>
                    }
                  </div>
                  @if (selectedCalendar === calendar) {
                    <div class="calendar-check">✓</div>
                  }
                </li>
              }
            </ul>
          }

          @if (!isLoadingCalendars && calendars.length === 0) {
            <div class="no-calendars">No calendars found</div>
          }
        </div>

        <!-- Meeting Type -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Meeting Type</mat-label>
          <mat-select
            [(ngModel)]="selectedMeetingType"
            (ngModelChange)="onMeetingTypeChange()"
            [disabled]="isLoadingMeetingTypes || isSending">
            @for (meetingType of meetingTypes; track meetingType.meetingTypeId) {
              <mat-option [value]="meetingType">
                {{ getMeetingTypeDisplayName(meetingType) }}
              </mat-option>
            }
          </mat-select>
          @if (!isLoadingMeetingTypes) {
            <mat-hint>Select the type of meeting</mat-hint>
          } @else {
            <mat-hint>Loading meeting types...</mat-hint>
          }
        </mat-form-field>

        <!-- Case Search -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Case</mat-label>
          <input
            matInput
            type="text"
            [(ngModel)]="caseSearchText"
            [matAutocomplete]="caseAutocomplete"
            [disabled]="isSending"
            placeholder="Search by case number or name..."
            (input)="onCaseSearchInput($event)">
          @if (selectedCase) {
            <button matSuffix mat-icon-button aria-label="Clear" (click)="clearCase()" [disabled]="isSending">
              <span class="clear-icon">×</span>
            </button>
          }
          <mat-autocomplete #caseAutocomplete="matAutocomplete" (optionSelected)="onCaseSelected($event.option.value)">
            @if (isSearchingCases) {
              <mat-option disabled><span class="searching-indicator">Searching...</span></mat-option>
            }
            @for (caseItem of caseSearchResults; track caseItem.caseNumber) {
              <mat-option [value]="caseItem">
                <div class="case-option">
                  <span class="case-number">{{ caseItem.caseNumber }}</span>
                  <span class="case-name">{{ caseItem.caseName }}</span>
                </div>
              </mat-option>
            }
            @if (!isSearchingCases && caseSearchResults.length === 0 && caseSearchText.length >= 3) {
              <mat-option disabled>No cases found</mat-option>
            }
          </mat-autocomplete>
          <mat-hint>Select a case to associate with this calendar link</mat-hint>
        </mat-form-field>

        <!-- Recipient -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Recipient</mat-label>
          <mat-select
            [(ngModel)]="selectedContact"
            (ngModelChange)="onRecipientChange()"
            [disabled]="!selectedCase || isLoadingContacts || isSending">
            @for (contact of availableRecipients; track contact.contactId) {
              <mat-option [value]="contact">{{ getContactDisplayName(contact) }}</mat-option>
            }
          </mat-select>
          @if (!selectedCase) {
            <mat-hint>Select a case first to load contacts</mat-hint>
          } @else if (isLoadingContacts) {
            <mat-hint>Loading contacts...</mat-hint>
          } @else if (contacts.length > 0) {
            <mat-hint>Select the recipient for this calendar link</mat-hint>
          } @else {
            <mat-hint>No contacts found for this case</mat-hint>
          }
        </mat-form-field>

        <!-- Attendees -->
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Attendees</mat-label>
          <mat-select
            multiple
            [(ngModel)]="selectedAttendees"
            [disabled]="!selectedCase || isLoadingContacts || isSending">
            <mat-select-trigger>
              {{ selectedAttendees.length > 0 ? getContactDisplayName(selectedAttendees[0]) : '' }}
              @if (selectedAttendees.length > 1) {
                <span class="attendee-extra">+{{ selectedAttendees.length - 1 }} more</span>
              }
            </mat-select-trigger>
            @for (contact of availableAttendees; track contact.contactId) {
              <mat-option [value]="contact">{{ getContactDisplayName(contact) }}</mat-option>
            }
          </mat-select>
          @if (!selectedCase) {
            <mat-hint>Select a case first to load contacts</mat-hint>
          } @else if (isLoadingContacts) {
            <mat-hint>Loading contacts...</mat-hint>
          } @else if (availableAttendees.length > 0) {
            <mat-hint>Select any additional attendees for this meeting</mat-hint>
          } @else {
            <mat-hint>No other contacts available for this case</mat-hint>
          }
        </mat-form-field>

        <!-- Email Content -->
        <div class="email-content-section">
          <label class="section-label">Email Content</label>
          <quill-editor
            [(ngModel)]="emailContent"
            [modules]="quillModules"
            [styles]="{ height: '200px' }"
            [disabled]="isSending || isLoadingTemplate"
            placeholder="Loading template..."
            (onEditorCreated)="onEditorCreated($event)">
          </quill-editor>
          @if (!isLoadingTemplate) {
            <div class="hint-text">Customize the email content sent to the recipient</div>
          } @else {
            <div class="hint-text">Loading email template...</div>
          }
        </div>

        <!-- File Attachments (signing meetings only) -->
        @if (showAttachments) {
          <div class="attachments-section">
            <label class="section-label">Attachments</label>
            <ngx-dropzone (change)="onFilesAdded($event)" [accept]="acceptedFileTypes" [disabled]="isSending">
              <ngx-dropzone-label>
                Drop files here or click to upload<br>
                <small>PDF or Word documents, up to 25 MB each (max 10 files)</small>
              </ngx-dropzone-label>
            </ngx-dropzone>

            @if (uploadedFiles.length > 0) {
              <ul class="file-list">
                @for (file of uploadedFiles; track file.name) {
                  <li class="file-item">
                    <span class="file-name">{{ file.name }}</span>
                    <span class="file-size">{{ formatFileSize(file.size) }}</span>
                    <button type="button" class="file-remove" aria-label="Remove file" (click)="onFileRemoved(file)" [disabled]="isSending">×</button>
                  </li>
                }
              </ul>
            }
          </div>
        }

      </div>

      <!-- ===================== SUMMARY RAIL ===================== -->
      <aside class="selection-rail">
        <div class="rail-summary">
          <span class="rail-eyebrow">Send Summary</span>

          <div class="rail-row">
            <span class="rail-key">From</span>
            <span class="rail-val">{{ selectedCalendar ? selectedCalendar.ownerName : '—' }}</span>
          </div>
          <div class="rail-divider"></div>

          <div class="rail-row">
            <span class="rail-key">Meeting</span>
            <span class="rail-val">
              {{ selectedMeetingType ? selectedMeetingType.name + ' · ' + selectedMeetingType.minuteLength + ' min' : '—' }}
            </span>
          </div>
          <div class="rail-divider"></div>

          <div class="rail-row">
            <span class="rail-key">Case</span>
            <span class="rail-val">{{ selectedCase ? selectedCase.caseName : '—' }}</span>
          </div>
          <div class="rail-divider"></div>

          <div class="rail-row">
            <span class="rail-key">To</span>
            <span class="rail-val">{{ selectedContact ? selectedContact.firstName + ' ' + selectedContact.lastName : '—' }}</span>
            @if (selectedContact) {
              <span class="rail-sub">{{ selectedContact.contactEmail }}</span>
            }
          </div>

          @if (selectedAttendees.length > 0) {
            <div class="rail-divider"></div>
            <div class="rail-row">
              <span class="rail-key">Attendees</span>
              @for (attendee of selectedAttendees; track attendee.contactId) {
                <span class="rail-val">{{ attendee.firstName }} {{ attendee.lastName }}</span>
              }
            </div>
          }

          <button class="rail-send" mat-raised-button color="primary" [disabled]="!isFormValid()" (click)="sendSelection()">
            @if (!isSending) {
              <span>Send Calendar Link</span>
            } @else {
              <span class="rail-sending">
                <mat-spinner diameter="20"></mat-spinner>
                Sending...
              </span>
            }
          </button>
        </div>

        @if (showAttachments) {
          <div class="rail-note">
            <div class="rail-note__head">Signing meeting</div>
            <p>Attachments are available because this is a signing meeting type. The recipient receives them with the booking link.</p>
          </div>
        }
      </aside>

    </div>
  </div>
</div>
